import React, { useRef, useState } from 'react';
import { TCountOfItems } from 'types';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import { Radio } from 'components/Radio/Radio';
import Text from 'components/Text/Text';

const countOfItemsOptions: TCountOfItems[] = ['12', '24', '48'];

const optionClassName = 'h-10 px-4 rounded flex whitespace-nowrap hover:bg-purple-01-new';

interface IProps<T extends string> {
	sortBy: T;
	sortOptions: { value: T; translationKey: string }[];
	countOfItems: TCountOfItems;
	testId?: string;
	onSortByChange: (sortBy: T) => void;
	onCountOfItemsChange: (countOfItems: TCountOfItems) => void;
}

export const SortAndCountDropdown = <T extends string>({
	sortBy,
	sortOptions,
	countOfItems,
	testId,
	onSortByChange,
	onCountOfItemsChange,
}: IProps<T>) => {
	const { t } = useTranslation('listing');
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const menu = (
		<div className="min-w-52 p-1 rounded bg-white-00-new shadow-400" data-test={testId && `${testId}Menu`}>
			<div className="h-10 px-4 flex items-center">
				<Text
					className="!text-grey-06-new"
					translationNameSpace="listing"
					translationKey="showLabel"
					size="xs"
				/>
			</div>
			{countOfItemsOptions.map((el) => (
				<Radio
					key={el}
					className={optionClassName}
					name="countOfItems"
					checked={el === countOfItems}
					testId={testId && `${testId}Count${el}`}
					onChange={() => onCountOfItemsChange(el)}>
					<Text translationKey={el} size="xs" />
				</Radio>
			))}
			<div className="h-1 px-4 flex items-center">
				<div className="h-px flex-1 bg-grey-05-new" />
			</div>
			<div className="h-10 px-4 flex items-center">
				<Text
					className="!text-grey-06-new"
					translationNameSpace="listing"
					translationKey="sortByLabel"
					size="xs"
				/>
			</div>
			{sortOptions.map((el) => (
				<Radio
					key={el.value}
					className={optionClassName}
					name="sortBy"
					checked={el.value === sortBy}
					testId={testId && `${testId}Sort${el.value}`}
					onChange={() => onSortByChange(el.value)}>
					<Text translationNameSpace="listing" translationKey={el.translationKey} size="xs" />
				</Radio>
			))}
		</div>
	);

	return (
		<div ref={containerRef} className="relative">
			<Dropdown
				overlay={menu}
				trigger={['click']}
				placement="bottomRight"
				visible={isOpen}
				destroyPopupOnHide={true}
				getPopupContainer={(triggerNode) => containerRef.current ?? triggerNode}
				onVisibleChange={setIsOpen}>
				<NewButton
					variant="outline"
					size="small"
					icon={true}
					iconLeft={<Icon name={IconName.filter} size="lg" />}
					ariaLabel={t('sortingLabel')}
					testId={testId}
				/>
			</Dropdown>
		</div>
	);
};
